class VariantSelector extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const dataScript = this.querySelector('[data-variant-data]');
    if (!dataScript) return;

    try {
      this.variantData = JSON.parse(dataScript.textContent);
    } catch {
      return;
    }

    this.variants = this.variantData.variants;
    this.productOptions = this.variantData.options;
    this.productId = this.dataset.productId;
    this.optionButtons = [...this.querySelectorAll('[data-option-value]')];
    this.selectedOptions = this.getInitialSelectedOptions();

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-option-value]');
      if (!button) return;

      const index = parseInt(button.dataset.optionIndex, 10);
      const value = button.dataset.optionValue;

      this.selectedOptions[index] = value;
      if (this.dataset.dependentOptions === 'true') this.resolveLaterOptions(index);

      const variant = this.findVariant(this.selectedOptions);
      this.fillMissingOptions(variant);

      this.updateButtonStates();
      this.updateAvailability();

      publish(PUB_SUB_EVENTS.variantChange, {
        productId: this.productId,
        variant,
      });
    });

    this.updateAvailability();
  }

  disconnectedCallback() {
    this.initialized = false;
  }

  getInitialSelectedOptions() {
    const options = [];
    if (!this.productOptions) return options;

    this.productOptions.forEach((option, index) => {
      const activeButton = this.querySelector(`[data-option-index="${index}"][aria-pressed="true"]`);
      if (activeButton) {
        options[index] = activeButton.dataset.optionValue;
      } else {
        options[index] = option.values?.[0] ?? '';
      }
    });

    return options;
  }

  findVariant(selectedOptions) {
    const exactMatch = this.variants.find((v) => v.options.every((opt, i) => opt === selectedOptions[i]));
    if (exactMatch) return exactMatch;

    const displayedIndices = this.getDisplayedIndices();

    const partialMatch = this.variants.find((v) =>
      v.options.every((opt, i) => {
        if (!displayedIndices.has(i)) return true;
        return opt === selectedOptions[i];
      }),
    );

    return partialMatch ?? null;
  }

  // A later option keeps its value only if it still exists after this change; otherwise the
  // first matching variant (in stock first) supplies it, e.g. a new range's first shade.
  resolveLaterOptions(changedIndex) {
    const optionCount = this.productOptions?.length ?? 0;
    for (let i = changedIndex + 1; i < optionCount; i++) {
      const prefix = this.selectedOptions.slice(0, i);
      const matchesPrefix = (v) => prefix.every((opt, j) => v.options[j] === opt);
      const stillExists = this.variants.some((v) => matchesPrefix(v) && v.options[i] === this.selectedOptions[i]);
      if (stillExists) continue;

      const fallback = this.variants.find((v) => matchesPrefix(v) && v.available) ?? this.variants.find(matchesPrefix);
      if (fallback) this.selectedOptions[i] = fallback.options[i];
    }
  }

  // With dependent options, a value is shown only when it exists under the options before it.
  valueExists(index, value) {
    if (this.dataset.dependentOptions !== 'true' || index === 0) return true;
    return this.variants.some(
      (v) => v.options[index] === value && this.selectedOptions.slice(0, index).every((opt, j) => v.options[j] === opt),
    );
  }

  fillMissingOptions(variant) {
    if (!variant) return;
    const displayedIndices = this.getDisplayedIndices();
    variant.options.forEach((opt, i) => {
      if (!displayedIndices.has(i)) {
        this.selectedOptions[i] = opt;
      }
    });
  }

  getDisplayedIndices() {
    return new Set(this.optionButtons.map((btn) => parseInt(btn.dataset.optionIndex, 10)));
  }

  // With dependent options, the card for a value reflects the variant under the options chosen before it.
  findVariantForOption(index, value) {
    const prefix = this.dataset.dependentOptions === 'true' ? this.selectedOptions.slice(0, index) : [];
    return (
      this.variants.find((v) => v.options[index] === value && prefix.every((opt, j) => v.options[j] === opt)) ?? null
    );
  }

  updateButtonStates() {
    this.optionButtons.forEach((btn) => {
      const index = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.optionValue;
      const isSelected = this.selectedOptions[index] === value;
      const isColor = btn.dataset.isColor === 'true';

      btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      if (btn.dataset.cardStyle === 'size-select') {
        btn.classList.toggle('hidden', !this.valueExists(index, value));
        const matchedVariant = this.findVariantForOption(index, value);
        const outOfStock = !matchedVariant?.available;

        btn.dataset.outOfStock = String(outOfStock);
        btn.classList.toggle('border-ink', isSelected);
        btn.classList.toggle('border-muted-grey/50', !isSelected);

        const labelEl = btn.querySelector('.variant-option__label');
        labelEl?.classList.toggle('text-ink', isSelected);
        labelEl?.classList.toggle('text-body-grey', !isSelected);

        const priceEl = btn.querySelector('[data-out-of-stock-hide]');
        const oosEl = btn.querySelector('[data-out-of-stock-show]');
        priceEl?.classList.toggle('hidden', outOfStock);
        priceEl?.classList.toggle('text-ink', isSelected);
        priceEl?.classList.toggle('text-body-grey', !isSelected);
        oosEl?.classList.toggle('hidden', !outOfStock);
        return;
      }

      if (btn.dataset.cardStyle === 'option-select') {
        btn.classList.toggle('border-ink', isSelected);
        btn.classList.toggle('text-ink', isSelected);
        btn.classList.toggle('border-muted-grey/50', !isSelected);
        btn.classList.toggle('text-body-grey', !isSelected);
        return;
      }

      if (isColor) {
        if (isSelected) {
          btn.classList.add('border-foreground', 'ring-2', 'ring-foreground/20');
          btn.classList.remove('border-border');
        } else {
          btn.classList.remove('border-foreground', 'ring-2', 'ring-foreground/20');
          btn.classList.add('border-border');
        }
      } else {
        const isFilled = btn.dataset.pillStyle === 'filled';
        if (isSelected) {
          btn.classList.add('border-foreground', 'bg-foreground', 'text-background');
          btn.classList.remove('border-border', 'border-foreground/20', 'bg-foreground/5');
        } else if (isFilled) {
          btn.classList.remove('border-foreground', 'bg-foreground', 'text-background', 'border-border');
          btn.classList.add('border-foreground/20', 'bg-foreground/5');
        } else {
          btn.classList.remove(
            'border-foreground',
            'bg-foreground',
            'text-background',
            'border-foreground/20',
            'bg-foreground/5',
          );
          btn.classList.add('border-border');
        }
      }
    });

    this.querySelectorAll('[data-selected-option]').forEach((label) => {
      const index = parseInt(label.dataset.selectedOption, 10);
      if (this.selectedOptions[index]) {
        label.textContent = this.selectedOptions[index];
      }
    });
  }

  updateAvailability() {
    const displayedIndices = this.getDisplayedIndices();

    this.optionButtons.forEach((btn) => {
      if (btn.dataset.cardStyle === 'size-select') return;

      const index = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.optionValue;

      const candidateSelection = [...this.selectedOptions];
      candidateSelection[index] = value;

      // Dependent options judge a value by the options before it only; later ones re-resolve on click.
      const isDependent = this.dataset.dependentOptions === 'true';
      const available = this.variants.some(
        (v) =>
          v.available &&
          v.options.every((opt, i) => {
            if (!displayedIndices.has(i) || (isDependent && i > index)) return true;
            return opt === candidateSelection[i];
          }),
      );

      btn.toggleAttribute('disabled', !available);
      btn.classList.toggle('opacity-40', !available);
      btn.classList.toggle('hidden', !this.valueExists(index, value));
    });
  }
}

if (!customElements.get('variant-selector')) {
  customElements.define('variant-selector', VariantSelector);
}
